import { graphql } from 'react-relay';
import { useRelayFetchQuery } from '../relay.utils';
import type { Contact_Pagination_Query$variables } from './__generated__/Contact_Pagination_Query.graphql';
import type { Contact_Select_Pagination_Query$variables } from './__generated__/Contact_Select_Pagination_Query.graphql';
import type { Contacts_Validate_Query } from './__generated__/Contacts_Validate_Query.graphql';

const ContactNodeFragment = graphql`
  fragment Contact_Node_Fragment on Contact {
    id
    databaseId
    created
    modified
    email
    firstname
    lastname
    company
    country
    ids
    jobTitle
    formEntries {
      edges {
        node {
          marketingConsent
          created
          form {
            node {
              title
              content
            }
          }
        }
      }
    }
  }
`;

const ContactSingleFragment = graphql`
  fragment Contact_Single_Fragment on Contact {
    ...Contact_Node_Fragment @relay(mask: false)
    phone
    addressLine1
    addressLine2
    addressLine3
    city
    county
    postalCode
    region
    ids
    properties {
      key
      value
    }
  }
`;

export type Contact_Pagination_Fragment$args = Contact_Pagination_Query$variables;

export * from './__generated__/Contact_Pagination_Fragment.graphql';
export const ContactPaginationFragment = graphql`
  fragment Contact_Pagination_Fragment on RootQuery
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    where: { type: "RootQueryToContactConnectionWhereArgs" }
  )
  @refetchable(queryName: "Contact_Pagination_Query") {
    contacts(after: $after, first: $first, where: $where) 
      @connection(key: "Contact_Pagination_contacts") {
        count
        __id
        edges {
          cursor
          node {
            ...Contact_Node_Fragment @relay(mask: false)
          }
        }
      }
  }
`;

export * from './__generated__/Contacts_Root_Query.graphql';
export const ContactsRootQuery = graphql`
  query Contacts_Root_Query {
    ...Contact_Pagination_Fragment @arguments(first: 24, after: null, where: null)
  }
`;

export type Contact_Select_Pagination_Fragment$args = Contact_Select_Pagination_Query$variables;

export * from './__generated__/Contact_Select_Pagination_Fragment.graphql';
export const ContactSelectPaginationFragment = graphql`
  fragment Contact_Select_Pagination_Fragment on RootQuery
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    where: { type: "RootQueryToContactConnectionWhereArgs" }
  )
  @refetchable(queryName: "Contact_Select_Pagination_Query") {
    contacts(after: $after, first: $first, where: $where) @connection(key: "Contact_Select_Pagination_contacts") {
      count
      __id
      edges {
        cursor
        node {
          id
          email
          firstname
          lastname
        }
      }
    }
  }
`;

export * from './__generated__/Contact_Single_Query.graphql';
export const ContactSingleQuery = graphql`
  query Contact_Single_Query($contactId: ID!) {
    contact(id: $contactId) {
      ...Contact_Single_Fragment @relay(mask: false)
    }
  }
`;

export * from './__generated__/Contact_Select_Query.graphql';
export const ContactSelectQuery = graphql`
  query Contact_Select_Query {
    ...Contact_Select_Pagination_Fragment @arguments(first: 100, after: null)
  }
`;

export const ContactsValidateQuery = graphql`
  query Contacts_Validate_Query($field: ValidateContactsFieldEnum!, $inputNumberArray: [Int!], $inputStringArray: [String!], $inputBoolean: Boolean, $caseInsensitive: Boolean) {
    validateContacts(field: $field, inputNumberArray: $inputNumberArray, inputStringArray: $inputStringArray, inputBoolean: $inputBoolean, caseInsensitive: $caseInsensitive) {
      id
      exists
      inputString
      inputNumber
      contact {
        id
        firstname
        lastname
        email
        company
        country
      }
    }
  }
`;

export const useValidateContactsQuery = () => useRelayFetchQuery<Contacts_Validate_Query>(ContactsValidateQuery);
